import { useEffect, useState } from 'react';
import {
  listMyAddons,
  createMyAddon,
  updateMyAddon,
  deleteMyAddon,
  getMyPendingChange,
  type ProAddon,
} from '../../lib/professional-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

const money = (n: number) => `£${Number(n || 0).toFixed(2)}`;
const formatDuration = (mins: number) => {
  if (!mins || mins < 1) return '';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest === 0 ? `${h}h` : `${h}h ${rest}m`;
};

const BLANK = { name: '', price: '', duration_minutes: '' };

/**
 * Pro-side management of their own add-ons (e.g. "Olaplex treatment").
 * Self-serve CRUD — blank by default. These show in a dedicated section
 * on the pro's public profile for Hair / Beauty & Wellness bookings.
 */
export function ProAddonsManager() {
  const [addons, setAddons] = useState<ProAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // null = no form open · 'new' = add form · <id> = editing that row
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState(BLANK);
  // Add-on edits by a live (APPROVED) pro are held for admin review as
  // `pending_addons` ops on the change request. We overlay them so a
  // just-submitted change is visible + marked pending instead of vanishing.
  const [pendingOps, setPendingOps] = useState<any[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [res, pc] = await Promise.all([
        listMyAddons(),
        getMyPendingChange().catch(() => ({ status: false, data: null })),
      ]);
      setAddons(res.data ?? []);
      const ops = (pc as any)?.data?.payload?.pending_addons;
      setPendingOps(Array.isArray(ops) ? ops : []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load your add-ons.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setEditingId('new');
    setDraft(BLANK);
  };
  const startEdit = (a: ProAddon) => {
    setEditingId(a.id);
    setDraft({
      name: a.name,
      price: String(a.price),
      duration_minutes: String(a.duration_minutes),
    });
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(BLANK);
  };

  const save = async () => {
    if (saving) return;
    const name = draft.name.trim();
    const price = Number(draft.price);
    const duration = parseInt(draft.duration_minutes, 10);
    if (!name) {
      toast.error('Enter an add-on name.');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error('Enter a valid price.');
      return;
    }
    if (!Number.isFinite(duration) || duration < 5) {
      toast.error('Enter how long it takes (at least 5 minutes).');
      return;
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        const res = await createMyAddon({
          name,
          price,
          duration_minutes: duration,
        });
        toast.success(
          res?.pending_review
            ? 'Add-on submitted — it will go live once an admin approves it.'
            : 'Add-on added.',
        );
      } else if (editingId) {
        const res = await updateMyAddon(editingId, {
          name,
          price,
          duration_minutes: duration,
        });
        toast.success(
          res?.pending_review
            ? 'Changes submitted — they will go live once an admin approves them.'
            : 'Add-on updated.',
        );
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your add-on.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: ProAddon) => {
    if (!window.confirm(`Remove "${a.name}"?`)) return;
    try {
      const res = await deleteMyAddon(a.id);
      toast.success(
        res?.pending_review
          ? 'Removal submitted — it will be removed once an admin approves it.'
          : 'Add-on removed.',
      );
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove the add-on.'));
    }
  };

  // Overlay admin-pending ops: update/delete keyed by live id (to badge the
  // row), creates rendered as extra "pending" rows (not in the live list yet).
  const opByLiveId = new Map<string, any>();
  for (const o of pendingOps) {
    if (o?.id && (o.op === 'update' || o.op === 'delete')) {
      opByLiveId.set(o.id, o);
    }
  }
  const pendingCreates = pendingOps.filter((o) => o?.op === 'create');
  const hasPending = pendingOps.length > 0;

  return (
    <section className="mt-12">
      <div className="bg-[#f2eee2] px-6 py-3 flex items-center justify-between">
        <p className="font-bold text-[14px] text-[#8e2a2f]">Your add-ons</p>
        {editingId === null ? (
          <button
            type="button"
            onClick={startAdd}
            className="h-[32px] px-4 rounded-[8px] bg-[#8e2a2f] text-white text-[12px] font-bold hover:bg-[#7a2228] transition-colors"
          >
            + Add add-on
          </button>
        ) : null}
      </div>

      <p className="px-6 pt-3 text-[12px] text-[#908e8e]">
        Extras clients can add to a booking (e.g. "Olaplex treatment"). You
        set the price and how long each takes. Clients pay the platform fee +
        VAT on top, the same as your services.
      </p>

      {hasPending ? (
        <div className="mx-6 mt-3 rounded-[10px] border border-[#f3d6a0] bg-[#fff7e6] px-4 py-3">
          <p className="text-[13px] text-[#9a6700]">
            You have add-on changes{' '}
            <span className="font-semibold">awaiting admin approval</span>.
            They&apos;re shown below marked{' '}
            <span className="font-semibold">Pending approval</span> and go live
            once our team approves them.
          </p>
        </div>
      ) : null}

      {loading ? (
        <p className="px-6 py-6 text-sm text-[#908e8e]">Loading…</p>
      ) : (
        <div className="divide-y divide-[#eee]">
          {addons.length === 0 &&
          editingId !== 'new' &&
          pendingCreates.length === 0 ? (
            <p className="px-6 py-6 text-sm text-[#908e8e]">
              You haven't added any add-ons yet.
            </p>
          ) : null}

          {addons.map((a) =>
            editingId === a.id ? (
              <AddonForm
                key={a.id}
                draft={draft}
                setDraft={setDraft}
                onSave={save}
                onCancel={cancel}
                saving={saving}
              />
            ) : (
              <div
                key={a.id}
                className="flex items-center justify-between gap-3 px-6 py-4"
              >
                <div className="min-w-0">
                  <p className="font-bold text-[15px] text-[#3a3335] truncate">
                    {a.name}
                    {opByLiveId.get(a.id)?.op === 'update' ? (
                      <span className="ml-2 inline-block rounded-full bg-[#fff7e6] px-2 py-0.5 text-[10px] font-bold text-[#9a6700] align-middle ring-1 ring-inset ring-[#f3d6a0]">
                        Edit pending approval
                      </span>
                    ) : opByLiveId.get(a.id)?.op === 'delete' ? (
                      <span className="ml-2 inline-block rounded-full bg-[#fde8e8] px-2 py-0.5 text-[10px] font-bold text-[#b42318] align-middle ring-1 ring-inset ring-[#f3b4b4]">
                        Removal pending approval
                      </span>
                    ) : null}
                  </p>
                  <p className="text-[13px] text-[#908e8e]">
                    {money(a.price)} · {formatDuration(a.duration_minutes)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(a)}
                    disabled={editingId !== null}
                    className="h-[34px] px-4 rounded-[8px] border border-[#ede8e9] text-[#3a3335] text-[12px] font-bold hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors disabled:opacity-50"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(a)}
                    disabled={editingId !== null}
                    className="h-[34px] px-4 rounded-[8px] border border-[#ede8e9] text-[#908e8e] text-[12px] font-bold hover:border-[#dc2626] hover:text-[#dc2626] transition-colors disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ),
          )}

          {/* Add-ons just CREATED that are still awaiting approval — not in
              the live list yet, so render them here so the pro sees their
              submission instead of an empty list. */}
          {pendingCreates.map((o, i) => (
            <div
              key={`pending-create-${i}`}
              className="flex items-center justify-between gap-3 bg-[#fffdf7] px-6 py-4"
            >
              <div className="min-w-0">
                <p className="font-bold text-[15px] text-[#3a3335] truncate">
                  {o.name}
                  <span className="ml-2 inline-block rounded-full bg-[#fff7e6] px-2 py-0.5 text-[10px] font-bold text-[#9a6700] align-middle ring-1 ring-inset ring-[#f3d6a0]">
                    Pending approval
                  </span>
                </p>
                <p className="text-[13px] text-[#908e8e]">
                  {money(o.price)} · {formatDuration(o.duration_minutes)}
                </p>
              </div>
            </div>
          ))}

          {editingId === 'new' ? (
            <AddonForm
              draft={draft}
              setDraft={setDraft}
              onSave={save}
              onCancel={cancel}
              saving={saving}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}

function AddonForm({
  draft,
  setDraft,
  onSave,
  onCancel,
  saving,
}: {
  draft: { name: string; price: string; duration_minutes: string };
  setDraft: (d: { name: string; price: string; duration_minutes: string }) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="px-6 py-4 bg-[#fcfaf7] space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
        <Field
          label="Name"
          placeholder="e.g. Olaplex treatment"
          value={draft.name}
          onChange={(v) => setDraft({ ...draft, name: v })}
        />
        <Field
          label="Price (£)"
          placeholder="15"
          inputMode="decimal"
          value={draft.price}
          onChange={(v) =>
            /^[0-9]*\.?[0-9]{0,2}$/.test(v) || v === ''
              ? setDraft({ ...draft, price: v })
              : undefined
          }
        />
        <Field
          label="Time (mins)"
          placeholder="30"
          inputMode="numeric"
          value={draft.duration_minutes}
          onChange={(v) =>
            /^[0-9]*$/.test(v)
              ? setDraft({ ...draft, duration_minutes: v })
              : undefined
          }
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="h-[40px] px-5 rounded-[8px] bg-[#8e2a2f] text-white text-[13px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-[40px] px-5 rounded-[8px] border border-[#ede8e9] text-[#908e8e] text-[13px] font-bold hover:text-[#3a3335] transition-colors disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'decimal' | 'numeric';
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-[#908e8e] mb-1">
        {label}
      </span>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-[44px] px-3 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
      />
    </label>
  );
}
